'use client'

import { useEffect, useState } from 'react'
import { useAuthStore } from '@/lib/store/auth-store'
import { useAuthGuard } from '@/hooks/use-auth-guard'
import { LoadingSpinner } from '@/components/loading-spinner'

export default function HomePage(): React.JSX.Element {
  const {
    isAuthenticated,
    isLoading,
    isRehydrated,
    hasCompleteAuthState,
  } = useAuthStore()
  const authGuard = useAuthGuard()
  const [isHydrated, setIsHydrated] = useState(false)

  // Handle client-side hydration
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Handle routing after complete hydration
  useEffect(() => {
    // Only navigate after both React hydration and Zustand rehydration are complete
    if (isHydrated && isRehydrated && !isLoading) {
      const hasComplete = hasCompleteAuthState()
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth-token') : null

      // Validate token expiration before navigating
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          const currentTime = Date.now() / 1000
          const isTokenExpired = payload.exp && payload.exp < currentTime

          if (isTokenExpired) {
            // Token is expired, clear everything
            localStorage.removeItem('auth-token')
            localStorage.removeItem('auth-store')
            authGuard.navigateToLogin()
            return
          }
        } catch {
          // Token is malformed, clear it
          localStorage.removeItem('auth-token')
          localStorage.removeItem('auth-store')
          authGuard.navigateToLogin()
          return
        }
      }

      // More strict validation - ensure we have both token and complete auth state
      // and that the auth state was properly loaded from server
      if (token && isAuthenticated && hasComplete) {
        authGuard.navigateToDashboard()
      } else {
        // Clear any inconsistent auth state
        if (token && (!isAuthenticated || !hasComplete)) {
          // Token exists but auth is incomplete - this suggests invalid token
          localStorage.removeItem('auth-token')
        }
        authGuard.navigateToLogin()
      }
    }
  }, [isHydrated, isRehydrated, isAuthenticated, isLoading, hasCompleteAuthState, authGuard])

  // Show loading while hydration is incomplete
  return (
    <div className="min-h-screen flex items-center justify-center"
         style={{
           backgroundImage: 'url(/main-bg.png)',
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           backgroundRepeat: 'no-repeat',
         }}>
      <LoadingSpinner size="lg" text="Loading..." />
    </div>
  )
}
